import yup from "yup";
import { toLower } from "lodash";
import * as GQL from "@bonliva-crm/graphql";
import { regexps } from "@bonliva-crm/common";
import locale, { defaultLocale } from "@bonliva-crm/locale";
import { Professions } from "@bonliva-crm/shared";
import { ConsultantFormData } from "./consultant-edit-form";

const forbiddenDomains = [
  "regionblekinge.se",
  "regiondalarna.se",
  "gotland.se",
  "regiongavleborg.se",
  "regionhalland.se",
  "regionjh.se",
  "rjl.se",
  "regionkalmar.se",
  "kronoberg.se",
  "norrbotten.se",
  "skane.se",
  "regionstockholm.se",
  "regionsormland.se",
  "regionuppsala.se",
  "regionvarmland.se",
  "regionvasterbotten.se",
  "rvn.se",
  "regionvastmanland.se",
  "regionorebrolan.se",
  "regionostergotland.se",
  "vgregion.se",
  "karolinska.se",
  "sll.se",
  "tiohundra.se",
  "slso.se",
  "akademiska.se",
  "ltkronoberg.se",
  "ltv.se",
  "orebroll.se",
  "lthalland.se",
  "ltkalmar.se",
  "ltblekinge.se",
  "ltdalarna.se",
  "vll.se"
];

const nullableString = yup.string().nullable(true);
const nullableBool = yup.boolean().nullable(true);
const nullableYearXFrom = yup
  .string()
  .matches(/(?:x)|^$|^\d{4}$/, locale.YYYYorX)
  .nullable(true);

const nullableYearFrom = yup
  .string()
  .matches(/^$|^\d{4}$/, locale.YYYY)
  .nullable(true);

const citySchema = nullableString.matches(
  regexps.onlyLettersAndSpaces,
  locale["Can contain only letters"]
);

const countrySchema = nullableString;

const streetNameSchema = nullableString.matches(
  regexps.streetNameWithDash,
  locale["Can contain only numbers or letters and hyphen character"]
);

const zipCodeSchema = nullableString.matches(
  regexps.zipCodeOrEmpty,
  locale["Can contain only numbers"]
);
const taxRateSchema = yup.string().test("name", locale.NumberBetween(1, 100), (value) => {
  if (!value) return true;
  const parsed = parseFloat(value);
  return parsed >= 1 && parsed <= 100;
});

export const address = yup.object<GQL.FormAddressInput>({
  city: citySchema,
  country: countrySchema,
  streetName: streetNameSchema,
  zipCode: zipCodeSchema
});

// eslint-disable-next-line @typescript-eslint/naming-convention
export const contactType = yup.object<GQL.ConsultantContactTypeSectionInput>({
  type: yup.string().nullable(true) as yup.Schema<GQL.LegalPersonType>,
  businessAddress: address.nullable(true),
  ssn: yup
    .string()
    .when("businessAddress.country", {
      is: (v) => v === defaultLocale.country,
      otherwise: yup.string(),
      then: yup
        .string()
        .matches(
          regexps.seOrgNumOrEmpty,
          locale["Can contain only numbers, XXXXXX-XXXX"]
        )
    })
    .nullable(true),
  companyName: nullableString,
  emailForSelfInvoicing: nullableString.email(locale["Invalid email address"]),
  rate: yup
    .string()
    .nullable(true)
    .when("type", {
      is: (v) => v === GQL.LegalPersonType.Employee,
      then: taxRateSchema,
      otherwise: yup.string()
    }),
  increasedRate: taxRateSchema.nullable(true),
  startDate: yup.date().when("increasedRate", {
    is: (v) => v,
    then: yup.date().nullable(true).required(locale.fieldIsMandatory(locale.StartDate)),
    otherwise: yup.date().nullable(true)
  }),
  endDate: yup.date().when("increasedRate", {
    is: (v) => v,
    then: yup.date().nullable(true).required(locale.fieldIsMandatory(locale.EndDate)),
    otherwise: yup.date().nullable(true)
  })
});

export const competitors = yup.object<GQL.ConsultantCompetitorsSectionInput>({
  items: yup.array(yup.string())
});

export const education = yup.object<GQL.ConsultantEducationSectionInput>({
  items: yup.array(
    yup.object({
      courseName: nullableString,
      courseLocation: nullableString,
      from: yup.date().nullable(true),
      to: yup.date().nullable(true)
    })
  )
});

export const contacts = yup.object<GQL.ConsultantContactsSectionInput>({
  cellPhone: nullableString
    .matches(regexps.fullPhoneNumber, {
      message: locale["should match +xxxxxxxxxx"],
      excludeEmptyString: true
    })
    .test(
      "is-same-as-phone",
      locale["The phone number is already entered"],
      function (value) {
        const sanitizedValue = value.replace(/\D/g, "");
        const sanitizedPhone = this.parent.phone.replace(/\D/g, "");
        return !(sanitizedValue && sanitizedPhone && sanitizedValue === sanitizedPhone);
      }
    ),
  email: nullableString
    .email(locale["Invalid email address"])
    .test("is-not-private-email", locale["Is not private email"], (value) => {
      if (value && value.includes("@")) {
        const domain = value.split("@")[1];
        return !forbiddenDomains.includes(toLower(domain));
      }
      return true;
    }),
  secondaryEmail: nullableString
    .email(locale["Invalid email address"])
    .test("is-not-private-email", locale["Is not private email"], (value) => {
      if (value && value.includes("@")) {
        const domain = value.split("@")[1];
        return !forbiddenDomains.includes(toLower(domain));
      }
      return true;
    }),
  initialContact: nullableString
    .oneOf(Object.keys(GQL.InitialContact), locale.FillSomething(locale.InitialContact))
    .required(locale.InitialContact) as yup.Schema<GQL.InitialContact>,
  selfRegisterOption: nullableString.oneOf(
    [...Object.keys(GQL.SelfRegisterOption), null],
    locale.FillSomething(locale.initialContact.SelfRegister)
  ) as yup.Schema<GQL.SelfRegisterOption | null>,
  phone: nullableString
    .matches(regexps.fullPhoneNumber, {
      message: locale["should match +xxxxxxxxxx"],
      excludeEmptyString: true
    })
    .test(
      "is-same-as-cell-phone",
      locale["The phone number is already entered"],
      function (value) {
        const sanitizedValue = value.replace(/\D/g, "");
        const sanitizedCellPhone = this.parent.cellPhone.replace(/\D/g, "");
        return !(
          sanitizedValue &&
          sanitizedCellPhone &&
          sanitizedValue === sanitizedCellPhone
        );
      }
    ),
  preferredCommunication: yup
    .array(yup.string().oneOf([...Object.keys(GQL.PreferredCommunication), null]))
    .notRequired() as yup.Schema<GQL.PreferredCommunication[]>,
  recommendedByConsultant: yup
    .object({
      label: yup.string(),
      value: yup.string()
    })
    .nullable(true)
});

export const emergencyContacts = yup.object<GQL.ConsultantEmergencyContactsSectionInput>({
  items: yup.array(
    yup.object({
      name: nullableString,
      phone: nullableString.matches(regexps.phoneNumber, {
        message: locale["only number"],
        excludeEmptyString: true
      }),
      relation: nullableString
    })
  )
});

const employeeNumber = yup.object<GQL.ConsultantEmployeeNumberSectionInput>({
  employeeNumber: yup.string(),
  employeeNumber2: yup.string(),
  employeeNumber3: yup.string()
});

const genericProfessionSchema = yup.object<GQL.GenericProfessionInput>({
  professionName: yup.string(),
  systems: yup.array<string>(),
  certifiedFrom: nullableYearFrom,
  specializations: yup.array<GQL.GenericSpecializationInput>(
    yup.object<GQL.GenericSpecializationInput>({
      name: nullableString,
      yearFrom: nullableYearFrom,
      interested: yup.boolean(),
      qualified: yup.boolean(),
      comment: nullableString
    })
  )
});

const doctorProfessionSchema = yup.object<GQL.DoctorProfessionInput>({
  professionName: yup.string(),
  systems: yup.array<string>(),
  certifiedFrom: nullableYearFrom,
  areasOfExpertise: yup.array<GQL.DoctorAreasOfExpertiseInput>(
    yup.object<GQL.DoctorAreasOfExpertiseInput>({
      name: yup.string(),
      qualified: yup.boolean(),
      interested: yup.boolean(),
      comment: nullableString,
      yearFrom: nullableYearFrom,
      specializations: yup.array<GQL.DoctorSpecializationInput>(
        yup.object<GQL.DoctorSpecializationInput>({
          name: nullableString,
          qualified: yup.boolean(),
          interested: yup.boolean(),
          comment: nullableString
        })
      )
    })
  )
});

const nurseProfessionInput = yup.object<GQL.NurseProfessionInput>({
  professionName: yup.string(),
  systems: yup.array<string>(),
  certifiedFrom: nullableYearFrom,
  areasOfExpertise: yup.array<GQL.NurseAreasOfExpertiseInput>(
    yup.object<GQL.NurseAreasOfExpertiseInput>({
      name: nullableString,
      yearFrom: nullableYearFrom,
      qualified: yup.boolean(),
      interested: yup.boolean(),
      comment: nullableString
    })
  ),
  specializations: yup.array<GQL.NurseSpecializationInput>(
    yup.object<GQL.NurseSpecializationInput>({
      name: nullableString,
      yearFrom: nullableYearFrom,
      qualified: yup.boolean(),
      interested: yup.boolean(),
      comment: nullableString
    })
  )
});

const teacherProfessionInput = yup.object<GQL.TeacherProfessionInput>({
  professionName: yup.string(),
  systems: yup.array<string>(),
  certifiedFrom: nullableYearFrom,
  areasOfExpertise: yup.array<GQL.TeacherAreasOfExpertiseInput>(
    yup.object<GQL.TeacherAreasOfExpertiseInput>({
      name: yup.string(),
      certifiedFrom: nullableYearFrom,
      selected: yup.boolean(),
      specializations: yup.array<GQL.TeacherSpecializationInput>(
        yup.object<GQL.TeacherSpecializationInput>({
          name: yup.string(),
          qualified: yup.boolean(),
          interestedIn: yup.boolean(),
          experience: nullableYearXFrom
        })
      )
    })
  ),
  schoolPlatforms: yup.array<string>(),
  digitalCompetence: yup.array<string>()
});

const socionomProfessionInput = yup.object<GQL.SocionomProfessionInput>({
  professionName: yup.string(),
  systems: yup.array<string>(),
  educations: yup.array<string>(),
  certifiedFrom: nullableYearFrom,
  areasOfExpertise: yup
    .array<GQL.SocionomAreasOfExpertiseInput>(
      yup.object<GQL.SocionomAreasOfExpertiseInput>({
        name: yup.string(),
        qualified: yup.boolean(),
        interested: yup.boolean(),
        comment: nullableString,
        yearFrom: nullableYearFrom,
        specializations: yup.array<GQL.SocionomSpecializationInput>(
          yup.object<GQL.SocionomSpecializationInput>({
            name: nullableString,
            qualified: yup.boolean(),
            interested: yup.boolean(),
            comment: nullableString
          })
        )
      })
    )
    .min(1, locale.SelectSomething(locale.AreaOfExpertise))
});

const psykologProfessionInput = yup.object<GQL.PsykologProfessionInput>({
  professionName: yup.string(),
  systems: yup.array<string>(),
  methods: yup.array<string>(),
  certifiedFrom: nullableYearFrom,
  specializations: yup.array<GQL.PsykologSpecializationInput>(
    yup.object<GQL.PsykologSpecializationInput>({
      name: yup.string(),
      yearFrom: nullableYearFrom,
      interested: yup.boolean(),
      qualified: yup.boolean(),
      comment: nullableString
    })
  )
});

const radioNurseProfessionInput = yup.object<GQL.RadioNurseProfessionInput>({
  professionName: yup.string(),
  systems: yup.array<string>(),
  machines: yup.array<string>(),
  certifiedFrom: nullableYearFrom,
  specializations: yup.array<GQL.RadioNurseSpecializationInput>(
    yup.object<GQL.RadioNurseSpecializationInput>({
      name: yup.string(),
      yearFrom: nullableYearFrom,
      interested: yup.boolean(),
      qualified: yup.boolean(),
      comment: nullableString
    })
  )
});

export const professions = yup.array(
  yup.lazy<{ professionName: string }>((v) => {
    switch (v.professionName) {
      case Professions.SE_ARBETSTERAPEUT:
      case Professions.SE_AUDIONOM:
      case Professions.SE_BARNMORSKA:
      case Professions.SE_BIOMEDICINSK_ANALYTIKER:
      case Professions.SE_DIETIST:
      case Professions.SE_FARMACEUTER:
      case Professions.SE_FYSIOTERAPEUT:
      case Professions.SE_HALSO_OCH_SJUKVARDKURATOR:
      case Professions.SE_INTERNREKRYTERING:
      case Professions.SE_MEDICINSKA_SEKRETERARE:
      case Professions.SE_PSYKOTERAPEUT:
      case Professions.SE_USK:
      case Professions.DK_JORDEMODER:
      case Professions.DK_RADIOGRAFER:
      case Professions.DK_SOCIAL_OG_SUNDHEDSASSISTENT:
        return genericProfessionSchema;

      case Professions.SE_LAKARE:
      case Professions.NO_LEGE:
      case Professions.DK_LAEGER:
      case Professions.FI_LAAKARI:
        return doctorProfessionSchema;

      case Professions.SE_SSK:
      case Professions.NO_SYKEPLEIE:
      case Professions.DK_SYGEPLEJERSKER:
      case Professions.FI_SAIRAANHOITAJA:
        return nurseProfessionInput;

      case Professions.SE_LARARE:
        return teacherProfessionInput;

      case Professions.SE_SOCIONOM:
        return socionomProfessionInput;

      case Professions.SE_PSYKOLOG:
        return psykologProfessionInput;

      case Professions.SE_RONTGENSJUKSKOTERSKA:
        return radioNurseProfessionInput;

      default:
        // fallback: treat it as generic
        return genericProfessionSchema;
    }
  })
);

export const languageSkills = yup.object<GQL.ConsultantLanguageSkillsSection>({
  items: yup
    .array(
      yup.object({
        name: yup.string().required(locale.missingLang).nullable(true),
        level: yup
          .string()
          .required(locale.SelectSomething(locale.LanguageProficiencyLevel))
          .oneOf(
            Object.keys(GQL.LanguageSkillLevel),
            locale.SelectSomething(locale.LanguageProficiencyLevel)
          ) as yup.Schema<GQL.LanguageSkillLevel>
      })
    )
    .default([])
    .nullable(true)
});

export const personal = yup.object<GQL.ConsultantPersonalSectionInput>({
  intro: yup.string().nullable(true),
  about: yup.string(),
  important: yup.string(),
  countries: yup.array(yup.string()).min(1, locale.SelectSomething(locale.Country)),
  firstComment: yup.string(),
  firstName: yup.string().required(locale.FillSomething(locale.FirstName)),
  hasDrivingLicence: nullableBool,
  lastName: yup.string().required(locale.FillSomething(locale.LastName)),
  norwegianSsn: nullableString,
  commentAuthorization: nullableString,
  isWorkedNorway: nullableBool,
  isApprovedAuthorization: nullableBool,
  hprNumber: yup.string().matches(regexps.hprNumber, locale["Can contain only numbers"]),
  ssn: yup
    .string()
    .nullable(true)
    .matches(regexps.ssn, {
      message: locale.FillSomething(
        `${locale["Personal number"]}, ${locale["Personal number format"]}`
      ),
      excludeEmptyString: true
    }),
  accommodationPreferences: yup.string().nullable(true),
  travelPreferences: yup.string(),
  care: yup.string()
});

export const prescriptionCode = yup.object<GQL.ConsultantPrescriptionCodeSectionInput>({
  code: nullableString.matches(regexps.onlyNumbers, locale["Can contain only numbers"])
});

export const workExperience = yup.object<GQL.ConsultantWorkExperienceSectionInput>({
  items: yup.array(
    yup.object({
      areaOfExpertise: nullableString,
      roles: yup.string(),
      from: yup.date().nullable(true),
      to: yup.date().nullable(true),
      workType: nullableString,
      workPlace: yup.string().default("").nullable(true)
    })
  )
});

export const consultantId = yup.mixed().nullable(false) as any;
export const isNew = yup.boolean().nullable(false);
export const status = yup.object<GQL.ConsultantStatus>().nullable(true);

export const validationSchema = yup.object<ConsultantFormData>({
  consultantId,
  isNew,
  status,
  address,
  competitors,
  contacts,
  contactType,
  education,
  emergencyContacts,
  employeeNumber,
  professions,
  languageSkills,
  personal,
  prescriptionCode,
  workExperience,
  responsiblePerson: yup.mixed(),
  extraCurriculum: yup.string().nullable(true),
  GDPRConsentRecords: yup.array(),
  knownSystems: yup.array()
});
